/**
 * Pure utility functions for the Home module.
 * No React, no side-effects — safe to import anywhere.
 */

import type { DateGroup, ScheduledMeeting } from "./types";
import { formatInstant } from "@/lib/timezone";

// ── Time formatting ───────────────────────────────────────────────────────────

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

/**
 * Formats a timestamp as a human-readable relative time string.
 * e.g. "Just now", "5m ago", "3h ago", "2d ago", "May 11"
 */
export function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60_000);
  const hours   = Math.floor(diff / 3_600_000);
  const days    = Math.floor(diff / 86_400_000);

  if (minutes < 1) return "Just now";
  if (hours   < 1) return `${minutes}m ago`;
  if (hours   < 24) return `${hours}h ago`;
  if (days    < 7) return `${days}d ago`;

  const d = new Date(timestamp);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/**
 * Formats an ISO datetime string as a localised time string.
 * e.g. "9:00 AM", "2:30 PM"
 */
export function formatMeetingTime(iso: string): string {
  return formatInstant(iso, {
    hour: "numeric",
    minute: "2-digit",
  });
}

// ── Calendar grouping ─────────────────────────────────────────────────────────

/**
 * Groups a flat list of meetings into date buckets sorted chronologically.
 * Within each bucket, meetings are sorted by scheduledTime ascending.
 */
export function groupMeetingsByDate(meetings: ScheduledMeeting[]): DateGroup[] {
  const todayStr = new Date().toDateString();
  const map = new Map<string, ScheduledMeeting[]>();

  for (const m of meetings) {
    const key = new Date(m.scheduledTime).toDateString();
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(m);
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
    .map(([key, ms]) => {
      const d = new Date(key);
      const isToday = key === todayStr;
      return {
        dateKey: key,
        label: isToday ? "Today" : d.toLocaleDateString("en-US", { weekday: "short" }),
        shortDate: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        isToday,
        meetings: [...ms].sort(
          (a, b) => new Date(a.scheduledTime).getTime() - new Date(b.scheduledTime).getTime(),
        ),
      };
    });
}

// ── Greeting ──────────────────────────────────────────────────────────────────

/** Returns a time-of-day greeting string. */
export function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

// ── Platform metadata ─────────────────────────────────────────────────────────

/** Meeting platform to connector id. Unlisted platforms fall back to a glyph. */
export const PLATFORM_CONNECTOR_IDS: Record<string, string> = {
  google_meet: "google-meet",
  zoom: "zoom",
  teams: "teams",
};

export const PLATFORM_LABELS: Record<string, string> = {
  google_meet: "Google Meet",
  zoom: "Zoom",
  teams: "Microsoft Teams",
};
